/// <amd-module name="TFS/GlobalBanner/Components/GlobalMessageBanner" />
import * as React from "react";
import {
    GlobalMessageLevel,
    GlobalMessagesChangedEvents,
    IGlobalMessageAction,
    IGlobalMessageBanner,
    IGlobalMessagesService
} from "TFS/GlobalBanner/GlobalMessagesService.Types";
import * as GlobalBannerResources from "TFS/GlobalBanner/Resources";
import { contributedButtonToButtonProps } from "VSS/Features/PlatformUI/ContributedButton";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssComponentContext, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";

interface IGlobalBannerComponentState {
    messageBanner?: IGlobalMessageBanner;
}

const telemetryArea = "GlobalMessageBanner";

export class GlobalBannerComponent extends VssComponent<{}, IGlobalBannerComponentState> {
    public static componentType = "globalBanner";

    private globalMessagesService: IGlobalMessagesService;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.globalMessagesService = context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        this.globalMessagesService.subscribe(this.onMessageBannerChanged, GlobalMessagesChangedEvents.GlobalMessageChangedEvent);

        const messageBanner = this.globalMessagesService.getBanner();
        this.state = { messageBanner };
    }

    public render(): JSX.Element | null {
        const { messageBanner } = this.state;

        if (!messageBanner) {
            return null;
        }

        const { level } = messageBanner;

        let severity = "Info";

        if (level === GlobalMessageLevel.success) {
            severity = "Success";
        } else if (level === GlobalMessageLevel.error) {
            severity = "Error";
        } else if (level === GlobalMessageLevel.warning) {
            severity = "Warning";
        }

        return (
            <div className="global-message-banner flex-row flex-grow">
                <WrappedComponent
                    dependencies={["ms.vss-features.ui-messagebar-content"]}
                    wrappedType="messageBar"
                    buttonProps={
                        messageBanner.buttons
                            ? messageBanner.buttons.map((b) => contributedButtonToButtonProps(this.context.pageContext, b))
                            : undefined
                    }
                    className="flex-grow"
                    messageClassName={messageBanner.componentType ? "flex-grow" : ""}
                    iconProps={messageBanner.customIcon ? { iconName: messageBanner.customIcon } : undefined}
                    onDismiss={messageBanner.dismissable !== false ? this.onMessageBannerClose : undefined}
                    severity={severity}
                >
                    {messageBanner.componentType ? (
                        <WrappedComponent
                            wrappedType={messageBanner.componentType}
                            dependencies={messageBanner.contentDependencies}
                            {...{ onDismiss: this.onMessageBannerClose, ...messageBanner.contentProperties }}
                        />
                    ) : (
                        <div className="flex-grow">
                            {messageBanner.message || (messageBanner.messageFormat && messageBanner.messageLinks) ? (
                                messageBanner.messageFormat && messageBanner.messageLinks ? (
                                    <FormatComponent elementType="span" format={messageBanner.messageFormat}>
                                        {messageBanner.messageLinks.map((link, index) =>
                                            link.supportsFPS ? (
                                                <FPSLink href={link.href}>{link.name}</FPSLink>
                                            ) : (
                                                <Link href={link.href} target="_blank" rel="noopener noreferrer" key={index}>
                                                    {link.name}
                                                </Link>
                                            )
                                        )}
                                    </FormatComponent>
                                ) : (
                                    <span>{messageBanner.message}</span>
                                )
                            ) : null}
                            {messageBanner.helpInfo ? (
                                <Link
                                    className="help-link font-size-ml"
                                    href={messageBanner.helpInfo.href}
                                    role={messageBanner.helpInfo.href ? undefined : "presentation"}
                                    tooltipProps={messageBanner.helpInfo.tooltip ? { text: messageBanner.helpInfo.tooltip } : undefined}
                                >
                                    <Icon iconName="Unknown" className="v-align-middle" />
                                </Link>
                            ) : null}
                            {messageBanner._links && messageBanner._links["learn"] ? (
                                <Link
                                    className="learn-link"
                                    href={messageBanner._links["learn"].href}
                                    onClick={() => this.onLearnLinkClicked(messageBanner)}
                                >
                                    {GlobalBannerResources.LearnMore}
                                </Link>
                            ) : null}
                        </div>
                    )}
                </WrappedComponent>
            </div>
        );
    }

    public componentWillUnmount() {
        this.globalMessagesService.unsubscribe(this.onMessageBannerChanged, GlobalMessagesChangedEvents.GlobalMessageChangedEvent);

        super.componentWillUnmount();
    }

    private onMessageBannerClose = (): void => {
        this.globalMessagesService.closeBanner();
    };

    private onMessageBannerChanged = (newMessageBanner: IGlobalMessageBanner | undefined): void => {
        this.setState({ messageBanner: newMessageBanner }, fireWindowResize);
    };

    private onLearnLinkClicked = (messageBanner: IGlobalMessageBanner): void => {
        if (messageBanner.settingId) {
            const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            telemetryService.publishEvent(telemetryArea, "bannerLearnMoreClicked", {
                settingId: messageBanner.settingId
            });
        }
    }
}

function fireWindowResize() {
    // Notify "slow" components like Monaco that available space may have changed
    const event = document.createEvent("Event");
    event.initEvent("resize", false, true);
    window.dispatchEvent(event);
}

// Register the global banner with the layout manager
VssComponent.register(GlobalBannerComponent.componentType, GlobalBannerComponent);

class GlobalActionsComponent extends VssComponent<{}, {}> {
    public static componentType = "globalActions";

    private globalMessagesService: IGlobalMessagesService;
    private layoutManager: IVssLayoutManager;
    private actions: IGlobalMessageAction[] | undefined;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.globalMessagesService = context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        this.layoutManager = context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        this.actions = this.globalMessagesService.getActions();

        if (this.actions && this.actions.length) {
            for (const action of this.actions) {
                this.layoutManager.renderCallout((onClose) => {
                    return (
                        <WrappedComponent
                            wrappedType={action.componentType}
                            dependencies={action.contentDependencies}
                            {...{ onClose, ...action.contentProperties }}
                        />
                    );
                });
            }
        }
    }

    public render(): JSX.Element | null {
        return null;
    }
}

// Register the global banner with the layout manager
VssComponent.register(GlobalActionsComponent.componentType, GlobalActionsComponent);
